import { useApolloClient } from "@apollo/client";
import { DataSourceType } from "@decentriq/graphql/dist/enums";
import {
  CreateDataConnectorJobDocument,
  DataConnectorJobFragment,
} from "@decentriq/graphql/dist/types";
import { Button } from "@mui/joy";
import saveAs from "file-saver";
import { type SnackbarKey } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApiCore } from "contexts";
import {
  useMediaDataRoom,
  useMediaDataRoomInsightsData,
} from "features/mediaDataRoom/contexts";
import { type MediaDataRoomJobResultTransform } from "features/mediaDataRoom/hooks";
import {
  MediaDataRoomJobInput,
  useMediaDataRoomJob,
  useMediaDataRoomLazyJob,
  useMediaDataRoomStateActionRequest,
} from "features/mediaDataRoom/hooks/v3";
import {
  type AdvertiserAudienceV3,
  ALL_BASE_USERS_AUDIENCE,
  type AudienceV3,
  type LookalikeAudience,
  type MediaDataRoomParticipant,
  MediaDataRoomVersion,
  type RuleBasedAudience,
  type SeedAudienceV3,
} from "features/mediaDataRoom/models";
import { mapMediaDataRoomErrorToSnackbar, useDataRoomSnackbar } from "hooks";
import { logError, logInfo } from "utils";
import {
  AdvertiserAudiencesContext,
  type AdvertiserAudiencesV3ContextValue,
  type AudienceByIdSelectorFn,
} from "../models";
import type * as raw from "./models";
import { useAudienceSizes } from "./useAudienceSizes";
import {
  type AudienceSourceRef,
  getAudienceSourceRef,
  getRawAudienceReference,
  mapAudienceV3ToRawAudience,
  mapRawAudienceToAudienceV3,
  mapRawSeedAudienceToSeedAudienceV3,
} from "./utils";

const AdvertiserAudiencesV3Wrapper: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { enqueueSnackbar, closeSnackbar } = useDataRoomSnackbar();
  const setErrorSnackbarId = useState<SnackbarKey | undefined>()[1];
  const {
    dataRoomId,
    isDeactivated,
    currentUserEmail,
    dataRoomName,
    participants,
    supportedFeatures: { canViewAudiences, canShareAudience },
  } = useMediaDataRoom(MediaDataRoomVersion.v3);
  const { publishedDatasetsHashes, session } = useMediaDataRoomInsightsData(
    MediaDataRoomVersion.v3
  );
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const { client } = useApiCore();
  const transformAudiencesJobResult = useCallback<
    MediaDataRoomJobResultTransform<AudienceV3[]>
  >(
    async (zip) => {
      const audiencesFile = zip.file("audiences.json");
      if (audiencesFile === null) {
        throw new Error("audiences.json not found in zip");
      }
      const audiencesFileStructure = JSON.parse(
        await audiencesFile.async("string")
      ) as raw.AudiencesFileStructure;
      return audiencesFileStructure.audiences.map((audience) =>
        mapRawAudienceToAudienceV3(audience, participants)
      );
    },
    [participants]
  );
  const transformSeedAudiencesJobResult = useCallback<
    MediaDataRoomJobResultTransform<SeedAudienceV3[]>
  >(async (zip) => {
    const audiencesFile = zip.file("audiences.json");
    if (audiencesFile === null) {
      throw new Error("audiences.json not found in zip");
    }
    const audiencesFileStructure = JSON.parse(
      await audiencesFile.async("string")
    ) as raw.SeedAudiencesFileStructure;
    return audiencesFileStructure.audiences.map(
      mapRawSeedAudienceToSeedAudienceV3
    );
  }, []);
  // Retrieve audiences
  const audiencesJobResult = useMediaDataRoomJob({
    input: MediaDataRoomJobInput.create(
      "getCustomAudiences",
      dataRoomId,
      publishedDatasetsHashes
    ),
    requestCreator: useCallback(
      () => ({ user: currentUserEmail }),
      [currentUserEmail]
    ),
    session,
    skip: !(
      Boolean(dataRoomId) &&
      !isDeactivated &&
      publishedDatasetsHashes.hasRequiredData &&
      canViewAudiences
    ),
    transform: transformAudiencesJobResult,
  });
  const audiences = useMemo(
    () => audiencesJobResult.computeResults,
    [audiencesJobResult.computeResults]
  );
  // Retrieve seed audiences
  const seedAudiencesJobResult = useMediaDataRoomJob({
    input: MediaDataRoomJobInput.create(
      "getSeedAudiences",
      dataRoomId,
      publishedDatasetsHashes
    ),
    requestCreator: useCallback(
      () => ({ user: currentUserEmail }),
      [currentUserEmail]
    ),
    session,
    skip: !(
      Boolean(dataRoomId) &&
      !isDeactivated &&
      publishedDatasetsHashes.hasRequiredData &&
      canViewAudiences
    ),
    transform: transformSeedAudiencesJobResult,
  });
  const seedAudiences = useMemo(
    () => seedAudiencesJobResult.computeResults,
    [seedAudiencesJobResult.computeResults]
  );
  const getAudienceById = useCallback<AudienceByIdSelectorFn>(
    (audienceId, includeSeedAudiences) => {
      const audience = (audiences ?? []).find(
        (audience) => audience.id === audienceId
      );
      if (audience) {
        return audience;
      }
      if (!includeSeedAudiences) {
        throw new Error(`Audience with id ${audienceId} not found`);
      }
      const seedAudience = (seedAudiences ?? []).find(
        (audience) => audience.id === audienceId
      );
      if (!seedAudience) {
        throw new Error(`Seed audience with id ${audienceId} not found`);
      }
      return seedAudience as unknown as AudienceV3;
    },
    [audiences, seedAudiences]
  );
  useEffect(() => {
    if (audiencesJobResult.error) {
      const snackbarId = enqueueSnackbar(
        ...mapMediaDataRoomErrorToSnackbar(
          audiencesJobResult.error,
          "Unable to fetch audiences"
        )
      );
      setErrorSnackbarId(snackbarId);
    } else {
      setErrorSnackbarId((snackbarId) => {
        if (snackbarId) {
          closeSnackbar(snackbarId);
        }
        return undefined;
      });
    }
  }, [
    audiencesJobResult.error,
    enqueueSnackbar,
    closeSnackbar,
    setErrorSnackbarId,
  ]);
  useEffect(() => {
    if (seedAudiencesJobResult.error) {
      const snackbarId = enqueueSnackbar(
        ...mapMediaDataRoomErrorToSnackbar(
          seedAudiencesJobResult.error,
          "Unable to fetch audiences"
        )
      );
      setErrorSnackbarId(snackbarId);
    } else {
      setErrorSnackbarId((snackbarId) => {
        if (snackbarId) {
          closeSnackbar(snackbarId);
        }
        return undefined;
      });
    }
  }, [
    seedAudiencesJobResult.error,
    enqueueSnackbar,
    closeSnackbar,
    setErrorSnackbarId,
  ]);
  const [isSavingAudience, setIsSavingAudience] = useState(false);
  const [createCustomAudience] = useMediaDataRoomStateActionRequest({
    dataRoomId,
    key: "createCustomAudience",
  });
  const mapAudienceToDraftCustomAudience = useCallback(
    (
      audience: RuleBasedAudience | LookalikeAudience | AdvertiserAudienceV3
    ) => {
      const { sourceRef, sourceRefKind }: AudienceSourceRef =
        audience.sourceRef === ALL_BASE_USERS_AUDIENCE.id
          ? {
              sourceRef: ALL_BASE_USERS_AUDIENCE.id,
              sourceRefKind: "base",
            }
          : getAudienceSourceRef(getAudienceById(audience.sourceRef, true));
      const getRawAudienceReferenceById = (audienceId: string) =>
        getRawAudienceReference(getAudienceById(audienceId, true));
      const mappedAudience = mapAudienceV3ToRawAudience(
        {
          ...audience,
          canBeViewedByCurrentUser: true,
          createdBy: currentUserEmail,
          doesDependOnLookalikeAudience: false,
          errorReport: null,
          isStale: false,
          sharedWith: [],
          sourceRef,
          sourceRefKind,
        },
        getRawAudienceReferenceById
      );
      return mappedAudience;
    },
    [currentUserEmail, getAudienceById]
  );
  // Create audience
  const saveAudience = useCallback(
    async (
      audienceToSave:
        | RuleBasedAudience
        | LookalikeAudience
        | AdvertiserAudienceV3
    ) => {
      try {
        setIsSavingAudience(true);
        const { definition, name, sharedWith } =
          mapAudienceToDraftCustomAudience(audienceToSave);
        const { audience: rawAudience } = await createCustomAudience({
          options: {
            session,
          },
          requestCreator: () => ({
            definition,
            name,
            sharedWith: sharedWith ?? [],
          }),
        });
        const audience = mapRawAudienceToAudienceV3(
          {
            kind: "CUSTOM",
            ...rawAudience,
          } as raw.CustomAudience,
          participants
        );
        audiencesJobResult.setCacheData((prev = []) => [...prev, audience]);
      } catch (error) {
        logInfo("Error saving audience", error, audienceToSave);
        throw error;
      } finally {
        setIsSavingAudience(false);
      }
    },
    [
      mapAudienceToDraftCustomAudience,
      createCustomAudience,
      session,
      participants,
      audiencesJobResult,
    ]
  );
  const { disableSizeEstimationForAudience, ...restOfAudienceSizes } =
    useAudienceSizes({
      audiences,
      dataRoomId,
      publishedDatasetsHashes,
      session,
    });
  // Get prerequisites
  const [getCustomAudiencePreqrequisites] = useMediaDataRoomStateActionRequest({
    dataRoomId,
    key: "getAudiencePrerequisites",
  });
  const getAudiencePreqrequisites = useCallback(
    async (audienceId: string): Promise<AudienceV3[] | undefined> => {
      const audiencePreqrequisites = await getCustomAudiencePreqrequisites({
        options: {
          session,
        },
        requestCreator: () => ({
          audienceId,
        }),
      });
      return audiencePreqrequisites.audiences.map((audience) =>
        mapRawAudienceToAudienceV3(
          {
            ...audience,
            kind: "CUSTOM",
          } as raw.CustomAudience,
          participants
        )
      );
    },
    [getCustomAudiencePreqrequisites, session, participants]
  );
  // Delete Audience
  const [isDeletingAudience, setIsDeletingAudience] = useState(false);
  const [deleteCustomAudience] = useMediaDataRoomStateActionRequest({
    dataRoomId,
    key: "deleteCustomAudience",
  });
  const deleteAudience = useCallback(
    async (audienceId: string) => {
      try {
        setIsDeletingAudience(true);
        const preqrequisites = await getAudiencePreqrequisites(audienceId);
        await deleteCustomAudience({
          options: {
            session,
          },
          requestCreator: () => ({
            audienceId,
            forceDeletePrerequisites: true,
          }),
        });
        audiencesJobResult.setCacheData((audiences = []) =>
          audiences.filter(
            ({ id }) =>
              ![
                audienceId,
                ...(preqrequisites?.map((audience) => audience.id) ?? []),
              ].includes(id)
          )
        );
      } catch (error) {
        logInfo("Error saving audience", error, audienceId);
        enqueueSnackbar(
          ...mapMediaDataRoomErrorToSnackbar(
            error,
            "Failed to delete audience."
          )
        );
      } finally {
        setIsDeletingAudience(false);
      }
    },
    [
      audiencesJobResult,
      deleteCustomAudience,
      enqueueSnackbar,
      session,
      getAudiencePreqrequisites,
    ]
  );
  // Share Audience
  const [updateCustomAudience] = useMediaDataRoomStateActionRequest({
    dataRoomId,
    key: "updateCustomAudience",
  });
  const [isSharingAudience, setIsSharingAudience] = useState(false);
  const shareAudience = useCallback(
    async (
      audienceId: string,
      participantGroups: MediaDataRoomParticipant[]
    ) => {
      try {
        setIsSharingAudience(true);
        await updateCustomAudience({
          options: {
            session,
          },
          requestCreator: () => ({
            audienceId,
            sharedWith: participantGroups.map(({ id }) => id),
          }),
        });
        audiencesJobResult.setCacheData((audiences = []) =>
          audiences.map((audience) =>
            audience.id === audienceId
              ? {
                  ...audience,
                  sharedWith: participantGroups,
                  status: participantGroups.length ? "published" : "ready",
                }
              : audience
          )
        );
        const audience = getAudienceById(audienceId);
        return audience;
      } catch (error) {
        logInfo("Error sharing audience", error, audienceId);
        enqueueSnackbar(
          ...mapMediaDataRoomErrorToSnackbar(error, "Failed to share audience.")
        );
        return null;
      } finally {
        setIsSharingAudience(false);
      }
    },
    [
      audiencesJobResult,
      enqueueSnackbar,
      getAudienceById,
      session,
      updateCustomAudience,
    ]
  );
  // Export audience
  const transformAudienceForDownload = useCallback<
    MediaDataRoomJobResultTransform<string>
  >(async (zip) => {
    const audienceUsersFile = zip.file("audience_users.csv");
    if (audienceUsersFile === null) {
      throw new Error("audience_users.csv not found in zip");
    }
    const audienceUsersCsv = await audienceUsersFile.async("string");
    if (!audienceUsersCsv) {
      throw new Error("Audience is empty");
    }
    return audienceUsersCsv;
  }, []);
  const [getAudienceUserList] = useMediaDataRoomLazyJob({
    input: MediaDataRoomJobInput.create(
      "getAudienceUserList",
      dataRoomId,
      publishedDatasetsHashes
    ),
    transform: transformAudienceForDownload,
  });
  const [isExportingAudience, setIsExportingAudience] = useState<
    Record<string, boolean>
  >({});
  const downloadAudience = useCallback(
    async (audienceId: string) => {
      try {
        if (!publishedDatasetsHashes.hasRequiredData) {
          throw new Error(
            "Getting audience requires both the seed and base datasets uploaded"
          );
        }
        setIsExportingAudience((current) => ({
          ...current,
          [audienceId]: true,
        }));
        const audience = getAudienceById(audienceId);
        const audienceFileContent = await getAudienceUserList({
          requestCreator: () => ({
            audienceRef: getRawAudienceReference(audience),
          }),
          updateInput: (input) => input.withResourceId(audienceId),
        });
        const filename = `${dataRoomName}-${audience?.name ?? ""}.csv`;
        const file = new File([audienceFileContent], filename, {
          type: "application/octet-stream;charset=utf-8",
        });
        saveAs(file);
      } catch (error) {
        enqueueSnackbar(
          ...mapMediaDataRoomErrorToSnackbar(
            error,
            "Unable to download audience"
          )
        );
      } finally {
        setIsExportingAudience((current) => ({
          ...current,
          [audienceId]: false,
        }));
      }
    },
    [
      dataRoomName,
      enqueueSnackbar,
      getAudienceById,
      getAudienceUserList,
      publishedDatasetsHashes.hasRequiredData,
    ]
  );
  const exportAudienceAsDataset = useCallback(
    async (audienceId: string) => {
      try {
        if (!publishedDatasetsHashes.hasRequiredData) {
          throw new Error(
            "Getting audience requires both the seed and base datasets uploaded"
          );
        }
        setIsExportingAudience((current) => ({
          ...current,
          [audienceId]: true,
        }));
        const audience = getAudienceById(audienceId);
        const job = await client.createJob(dataRoomId, {
          getAudienceUserList: {
            audienceRef: getRawAudienceReference(audience),
          },
          kind: "media",
        });
        const filename = `${dataRoomName}-${audience?.name ?? ""}.csv`;
        await apolloClient.mutate({
          mutation: CreateDataConnectorJobDocument,
          update: (cache, response) => {
            cache.modify({
              fields: {
                dataConnectorJobs: (existing = {}) => {
                  const datasetImportRef = cache.writeFragment({
                    data: response.data?.dataConnectorJob.create.record,
                    fragment: DataConnectorJobFragment,
                  });
                  return {
                    ...existing,
                    nodes: [datasetImportRef, ...(existing?.nodes || [])],
                  };
                },
              },
            });
          },
          variables: {
            input: {
              config: JSON.stringify({
                jobId: job.id,
                zipPath: "audience_users.csv",
              }),
              payload: {
                importDataset: {
                  datasetName: filename,
                  name: filename,
                  sourceType: DataSourceType.ComputeV2,
                },
              },
            },
          },
        });
        enqueueSnackbar(
          `"${audience?.name ?? ""}" result is being stored. Please check the status in the 'Datasets' page.`,
          {
            action: (
              <Button onClick={() => navigate("/datasets/external")}>
                Go to Datasets
              </Button>
            ),
            persist: true,
          }
        );
      } catch (error) {
        enqueueSnackbar(
          ...mapMediaDataRoomErrorToSnackbar(error, "Unable to export audience")
        );
      } finally {
        setIsExportingAudience((current) => ({
          ...current,
          [audienceId]: false,
        }));
      }
    },
    [
      apolloClient,
      client,
      dataRoomId,
      dataRoomName,
      enqueueSnackbar,
      getAudienceById,
      navigate,
      publishedDatasetsHashes.hasRequiredData,
    ]
  );
  const doesAudienceDependOnLookalikeAudience = useCallback(
    (audienceId: string) => {
      try {
        return getAudienceById(audienceId).doesDependOnLookalikeAudience;
      } catch (error) {
        logError("Error getting audience", error, audienceId);
        return false;
      }
    },
    [getAudienceById]
  );
  const canCurrentUserDeleteAudience = useCallback(
    (audience: AudienceV3) => audience.createdBy === currentUserEmail,
    [currentUserEmail]
  );
  const canCurrentUserShareAudience = useCallback(
    (audience: AudienceV3) =>
      canShareAudience &&
      audience.createdBy === currentUserEmail &&
      !audience.isStale &&
      !restOfAudienceSizes.audienceSizes[audience.id]?.error,
    [currentUserEmail, canShareAudience, restOfAudienceSizes.audienceSizes]
  );
  const contextValue = useMemo<AdvertiserAudiencesV3ContextValue>(
    () => ({
      audienceSizes: {
        disableSizeEstimationForAudience,
        ...restOfAudienceSizes,
      },
      audiences: audiencesJobResult,
      canCurrentUserDeleteAudience,
      canCurrentUserShareAudience,
      deleteAudience,
      doesAudienceDependOnLookalikeAudience,
      downloadAudience,
      exportAudienceAsDataset,
      getAudienceById,
      getAudiencePreqrequisites,
      getRawAudienceRef: getRawAudienceReference,
      isDeletingAudience,
      isExportingAudience,
      isSavingAudience,
      isSharingAudience,
      mapAudienceToDraftCustomAudience,
      saveAudience,
      seedAudiences: seedAudiencesJobResult,
      shareAudience,
      version: MediaDataRoomVersion.v3,
    }),
    [
      seedAudiencesJobResult,
      audiencesJobResult,
      doesAudienceDependOnLookalikeAudience,
      deleteAudience,
      disableSizeEstimationForAudience,
      downloadAudience,
      exportAudienceAsDataset,
      getAudiencePreqrequisites,
      isExportingAudience,
      isSharingAudience,
      mapAudienceToDraftCustomAudience,
      isSavingAudience,
      isDeletingAudience,
      shareAudience,
      saveAudience,
      canCurrentUserShareAudience,
      getAudienceById,
      canCurrentUserDeleteAudience,
      restOfAudienceSizes,
    ]
  );
  return (
    <AdvertiserAudiencesContext.Provider value={contextValue}>
      {children}
    </AdvertiserAudiencesContext.Provider>
  );
};
AdvertiserAudiencesV3Wrapper.displayName = "AdvertiserAudiencesV3Wrapper";

export default AdvertiserAudiencesV3Wrapper;
